import { useState, useEffect, useRef } from "react";
import { bucketUrlPrefix } from "common/config";
import ScrollContainer from "react-indiana-drag-scroll";
import { toSentenceCase } from "../../../helpers/stringUtils";
import { NameInitialsAvatar } from "react-name-initials-avatar";
import { CaretDown } from "phosphor-react";
import { cn } from "@/lib/utils";
import ImageListViewer from "@/components/ImageListViewer/ImageListViewer";
import { VenueReview } from "common/types";
import AnalyticsService from "@/services/analytics";

function BusinessReview({
  review,
  onFlag,
}: {
  review: VenueReview;
  onFlag: (review: VenueReview) => void;
}) {
  const [showFullText, setShowFullText] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  const [showImageList, setShowImageList] = useState<{
    show: boolean;
    current: number;
  }>({
    show: false,
    current: 0,
  });

  const toggleShowFullText = () => {
    setShowFullText((prev) => !prev);
  };

  useEffect(() => {
    if (textRef.current) {
      // Check if the content is overflowing by comparing scroll height and client height
      setIsTruncated(
        textRef.current.scrollHeight > textRef.current.clientHeight
      );
    }
  }, [review.notes]);

  const handleOpenFlagTracking = () => {
    AnalyticsService.trackGAEventHandler("flagReviewReportOpen");
    AnalyticsService.trackMx("flagReviewReportOpen", {});
  };

  return (
    <article className="rounded-2xl bg-primary-50 dark:bg-[#313A71]">
      <div className="p-4">
        <div className="flex items-start gap-4">
          {review.userProfilePhoto ? (
            <img
              className="w-14 h-14 rounded-[10px] shadow-md object-cover"
              src={bucketUrlPrefix + review.userProfilePhoto}
              alt={review.userFirstName}
            />
          ) : (
            <NameInitialsAvatar
              name={review.user?.name?.split(" ")[0]}
              bgColor={
                review?.userProfileColorCode
                  ? review.userProfileColorCode
                  : "#8751FF"
              }
              textColor="white"
              textSize="20px"
              size="54px"
              borderColor={
                review?.userProfileColorCode
                  ? review.userProfileColorCode
                  : "#8751FF"
              }
            />
          )}
          <div>
            <h2 className="desktop-h4-medium-semi-bold text-primary-800 dark:text-primary-150">
              {review.userFirstName}
            </h2>
            <p className="paragraph-small-medium !text-neutral-600 dark:!text-neutral-300">
              {review.timeAgo}
            </p>
          </div>
          <div className="flex-1 items-start justify-end flex mt-1">
            <button
              onClick={() => {
                handleOpenFlagTracking();
                onFlag(review);
              }}
            >
              <img src="/images/Flag.svg" alt="" width={24} />
            </button>
          </div>
        </div>

        {review.userDisabilityVisible == "1" &&
          review.userAbility.length > 0 && (
            <div className="mt-3">
              <span className="inline-flex items-center px-3 py-1 rounded-3xl bg-orange-300 paragraph-small-medium text-primary-800">
                {review.userAbility.split(",")[0]}
              </span>
            </div>
          )}

        <p
          ref={textRef}
          className={`mt-4 paragraph-small-regular text-neutral-700 dark:text-neutral-300 ${
            showFullText ? "" : "line-clamp-3"
          }`}
        >
          {toSentenceCase(review.notes)}
        </p>

        {isTruncated && (
          <button
            onClick={toggleShowFullText}
            className="!text-primary-500 dark:!text-primary-300 button-small-semi-bold mt-2 hover:underline flex flex-row justify-center items-center"
          >
            {showFullText ? "Read Less" : "Read More"}
            <CaretDown
              size={20}
              className={cn("ml-2", showFullText ? "rotate-180" : "")}
              weight="bold"
            />
          </button>
        )}
      </div>

      <div className="pl-4 mb-4">
        <ScrollContainer className="flex flex-row space-x-2">
          {review.reviewPhotos.map((photo, index) => (
            <div
              key={index}
              onClick={() => {
                setShowImageList({
                  show: true,
                  current: index,
                });
              }}
            >
              <img
                src={bucketUrlPrefix + photo.path}
                className="w-24 h-24 rounded-lg"
              />
            </div>
          ))}
        </ScrollContainer>
      </div>
      <ImageListViewer
        title="Photos of Reviews"
        currentImage={showImageList.current}
        images={review.reviewPhotos.map((photo) => ({
          id: photo.path,
          url: bucketUrlPrefix + photo.path,
          username: review.user.name,
          timeAgo: review.timeAgo,
          note: photo.altText,
        }))}
        visible={showImageList.show}
        onClose={() => {
          setShowImageList({
            show: false,
            current: 0,
          });
        }}
      />
    </article>
  );
}

export default BusinessReview;
